//preloader.js
import { WORLD_ORIGIN, GRAD_OFFSETS, PRELOADER } from './constants';
import Utils from './utils';
import { beginCamera, endCamera } from './camera';

export function createPreloader({
  imageCache,
  stageById,
  invalidate,
  onReveal,
} = {}) {
  const { GRAD_INTERVAL, GRAD_FADE_SPEED, GRAD_OUT_SPEED, PIECE_IN_SPEED } =
    PRELOADER;

  // 그래디언트 이미지
  const gradRecs = Array.from({ length: 7 }, (_, i) =>
    imageCache.load(`/images/preloader_p${i + 1}.png`),
  );
  const gradAlphas = Array(7).fill(0);
  let gradIndex = 0;
  let gradTimer = 0;

  // 각 막 글자조각 이미지
  const preloadPieces = [];
  function initPieces() {
    preloadPieces.length = 0;
    for (const piece of stageById.values()) {
      preloadPieces.push({
        piece,
        alpha: 0,
        rec: imageCache.load(piece.src),
      });
    }
  }
  initPieces();

  // 상태 관리
  let mode = 'preload';
  let done = false;

  // 이미지 게이트
  let started = false;
  function start() {
    started = true;
  }

  function reset() {
    mode = 'preload';
    done = false;
    started = false;
    gradAlphas.fill(0);
    gradIndex = 0;
    gradTimer = 0;
    initPieces();
    invalidate?.();
  }

  function updatePreload(dt) {
    gradTimer += dt;

    while (gradIndex < 7 && gradTimer >= GRAD_INTERVAL) {
      gradTimer -= GRAD_INTERVAL;
      gradIndex++;
    }

    for (let i = 0; i < gradIndex; i++) {
      gradAlphas[i] = Utils.lerpExp(gradAlphas[i], 1, GRAD_FADE_SPEED, dt);
    }

    if (gradIndex >= 7 && gradAlphas[6] > 0.98) {
      mode = 'reveal';
      onReveal?.();
    }
  }

  function updateReveal(dt) {
    // 그래디언트 페이드 아웃
    for (let i = 0; i < 7; i++) {
      gradAlphas[i] = Utils.lerpExp(gradAlphas[i], 0, GRAD_OUT_SPEED, dt);
    }

    // 글자조각 페이드 인
    for (const p of preloadPieces) {
      p.alpha = Utils.lerpExp(p.alpha, 1, PIECE_IN_SPEED, dt);
    }

    // 가장 느린 것 기준으로 종료
    let maxGrad = 0;
    for (let i = 0; i < 7; i++) maxGrad = Math.max(maxGrad, gradAlphas[i]);

    let minPiece = 1;
    for (const p of preloadPieces) minPiece = Math.min(minPiece, p.alpha);

    if (maxGrad < 0.02 && minPiece > 0.98) {
      done = true;
    }
  }

  function update(dt) {
    if (done || !started) return false;

    const prevMode = mode;
    const prevDone = done;

    let changed = false;

    const prevGradIndex = gradIndex;
    const prevGradTimer = gradTimer;

    if (mode === 'preload') updatePreload(dt);
    else updateReveal(dt);

    if (mode !== prevMode || done !== prevDone) changed = true;
    if (gradIndex !== prevGradIndex) changed = true;
    if (gradTimer !== prevGradTimer) changed = true;

    if (!done) changed = true;

    return changed;
  }

  function draw(ctx, viewport, camera) {
    if (!started) return;

    beginCamera(ctx, camera, viewport);

    ctx.globalCompositeOperation = 'source-over';

    // 그래디언트
    for (let i = 0; i < 7; i++) {
      const rec = gradRecs[i];
      if (!rec.ready) continue;

      const a = gradAlphas[i];
      if (a <= 0.001) continue;

      const off = GRAD_OFFSETS[i];

      ctx.save();
      ctx.globalAlpha = a;
      ctx.drawImage(
        rec.img,
        WORLD_ORIGIN.x + off.x,
        WORLD_ORIGIN.y + off.y,
        rec.w,
        rec.h,
      );
      ctx.restore();
    }

    // 글자조각
    for (const p of preloadPieces) {
      if (p.alpha <= 0.001) continue;

      const piece = p.piece;
      const rec = p.rec;
      if (!rec.ready) continue;

      ctx.save();
      ctx.globalAlpha = p.alpha;
      ctx.drawImage(
        rec.img,
        piece.baseX,
        piece.baseY,
        rec.w * piece.scale,
        rec.h * piece.scale,
      );
      ctx.restore();
    }

    // ctx.globalAlpha = 1;
    endCamera(ctx);
  }

  function isDone() {
    return done;
  }

  return {
    reset,
    start,
    update,
    draw,
    isDone,
  };
}
